import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function IslamicGuide65() {
  const contentRef = useRef(null);
  const titleText = "Bitcoin & The Abolition of Riba";
  const links = {
    prev: "/islamicguide-64",
    index: "/indexislamic-guide",
    next: "/islamicguide-66",
  };

  const questions = [
    {
      text: "1. What is the primary way new fiat money is introduced into the global monetary system?",
      options: [
        { value: "A", label: "A. Through proof of work" },
        { value: "B", label: "B. By issuing gold-backed currency" },
        { value: "C", label: "C. Through the creation of usurious debt" },
        { value: "D", label: "D. By mining natural resources" },
      ],
      correct: "C",
    },
    {
      text: "2. Why does the fiat monetary system discourage savings?",
      options: [
        { value: "A", label: "A. Because savings are taxed at higher rates" },
        {
          value: "B",
          label: "B. Because inflation continuously reduces purchasing power",
        },
        {
          value: "C",
          label: "C. Because fiat money is backed by gold reserves",
        },
        { value: "D", label: "D. Because savings accounts are prohibited" },
      ],
      correct: "B",
    },
    {
      text: "3. How does Bitcoin differ from fiat money in its creation process?",
      options: [
        { value: "A", label: "A. It is printed by central planners" },
        { value: "B", label: "B. It is issued by commercial banks" },
        {
          value: "C",
          label:
            "C. It is created through proof of work using energy and computation",
        },
        { value: "D", label: "D. It is borrowed into existence as debt" },
      ],
      correct: "C",
    },
    {
      text: "4. In what way does Bitcoin align with Islamic principles regarding Riba?",
      options: [
        { value: "A", label: "A. It allows unlimited supply like fiat" },
        { value: "B", label: "B. It is centrally controlled by governments" },
        {
          value: "C",
          label:
            "C. It is capped at 21 million and cannot be inflated, encouraging fair trade and savings",
        },
        {
          value: "D",
          label: "D. It is dependent on continuous interest-based lending",
        },
      ],
      correct: "C",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={65}
        timerMinutes={60}
        currentLevel={5}
        unlockedBadges={9}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 65</b>! Today we explore one of the most critical
        topics in the journey of understanding money from an Islamic
        perspective: the relationship between Riba and the modern fiat monetary
        system, and how Bitcoin offers a way out of this oppressive cycle. Riba,
        or usury, is condemned in the strongest terms in the Qur’an and Sunnah,
        yet the global financial order that dominates our lives is built
        entirely upon it. Every new unit of fiat money is born out of debt,
        ensuring that exploitation and injustice remain embedded at the heart of
        economic life. For centuries, Muslims had no real alternative to this
        system and were left with little choice but to participate in it.
        However, with the rise of Bitcoin, a monetary network free of debt and
        usury, the possibility of building a just and ethical economy has
        re-emerged. In this lesson, we will study how Bitcoin’s fixed supply,
        proof-of-work creation, and inherent resistance to inflation can empower
        Muslims to move towards a system that eliminates Riba and restores
        fairness in trade and savings.
        <br />
        <br />
        <b>The Grave Reality of Riba in the Modern World</b>
        <br />
        <br />
        Riba, or usury, has been identified in Islam as one of the gravest sins,
        so severe that it is mentioned in the Quran with unparalleled
        condemnation. Allah says:
        <br />
        “Those who consume Riba will not stand [on the Day of Resurrection]
        except as one stands who is being beaten by Satan into insanity. That is
        because they say, ‘Trade is just like Riba.’ But Allah has permitted
        trade and has forbidden Riba. So whoever has received an admonition from
        his Lord and desists may have what is past, and his affair rests with
        Allah. But whoever returns to [dealing in Riba] – those are the
        companions of the Fire; they will abide eternally therein.” (Quran
        2:275).
        <br />
        This verse makes it abundantly clear that Riba is not a minor error but
        a deliberate defiance of the divine order. Another verse intensifies
        this warning:
        <br />
        “O you who have believed, fear Allah and give up what remains due to you
        of Riba, if you should be believers. And if you do not, then be informed
        of a war from Allah and His Messenger. But if you repent, you may have
        your principal – [thus] you do no wrong, nor are you wronged.” (Quran
        2:278-279).
        <br />
        These words leave no room for compromise. To persist in Riba is to
        engage in open hostility against Allah and His Messenger. Furthermore,
        the Prophet Muhammad (peace be upon him) said: <br />
        “Allah has cursed the one who consumes Riba, the one who gives it, the
        one who records it, and the two who witness it. They are all the same.”
        (Sahih Muslim).
        <br />
        This comprehensive curse shows how Riba corrupts the entire ecosystem of
        financial dealings, affecting both giver and receiver, and extending
        even to facilitators and enablers.
        <br />
        Yet, despite this clear prohibition, the modern global monetary system
        is built almost entirely on Riba. The mechanism of fiat money creation
        is tied intrinsically to debt, which accrues interest. When new fiat
        currency is issued, it is not backed by gold, silver, or tangible
        assets, but rather created out of thin air through credit expansion.
        Central banks lend money to commercial banks at interest, and these
        banks lend to individuals, corporations, and governments at further
        interest. This endless cycle enslaves humanity in perpetual debt, and
        Islamic societies that are part of the global economy are inevitably
        ensnared in this cycle. Fiat money does not simply enable Riba but
        institutionalizes it. Debt becomes the engine of the economy, and
        inflation becomes its unavoidable by-product. When money is created from
        debt, its very existence represents a contract of Riba. As fiat supply
        expands without real backing, the purchasing power of people’s earnings
        diminishes, effectively stealing from them through inflation. A worker
        may save his earnings diligently, only to discover that the money buys
        less the following year. Thus, Riba in the fiat system is not only the
        explicit interest on loans but also the silent theft of inflation. Both
        are deeply unjust, violating the Quranic principle:
        <br />
        “…and do not consume one another’s wealth unjustly…” (Quran 2:188).
        <br />
        The modern fiat system leaves Muslims trapped in the very system that
        Islam commands them to avoid, creating a contradiction between faith and
        practice that has long appeared inescapable.
        <br />
        <br />
        <b>Fiat Money and the Trap of Debt</b>
        <br />
        <br />
        Fiat money is a system that inherently encourages debt while
        discouraging savings. Since fiat currencies lose value over time through
        inflation, individuals and businesses are incentivized to borrow and
        spend today rather than save for the future. Governments themselves set
        this pattern by continuously borrowing and running deficits, rolling
        over debts that can never truly be repaid. This culture of debt runs
        counter to the Islamic principle that encourages moderation, delayed
        gratification, and the preservation of wealth.The Prophet Muhammad
        (peace be upon him) said:
        <br />
        “The delay in repayment by a rich person is injustice.” (Sahih Bukhari
        and Sahih Muslim).
        <br />
        If delaying repayment is a form of injustice, then what can be said
        about entire global systems that encourage endless borrowing without
        accountability? In contrast, Islam encourages savings, wealth
        preservation, and trade built upon real assets and honest labor, all of
        which are undermined by fiat.
        <br />
        In the fiat system, new money is brought into existence not through work
        or production, but through the expansion of debt. Central planners
        extend credit, and this credit multiplies through fractional reserve
        banking, where banks lend far more than they hold in deposits. Thus,
        every unit of new fiat currency is born in usurious debt, and interest
        must be paid on it, even though the system never creates enough money to
        repay both the principal and the interest simultaneously. This design
        guarantees that there will always be losers, defaults, bankruptcies, and
        instability. The poor and middle classes are disproportionately harmed,
        as they are forced to borrow in a currency that loses value and repaid
        with inflated sums, while the wealthy who control the issuance of money
        benefit.
        <br />
        This cycle is an inversion of Islamic justice. The Quran condemns unjust
        enrichment:
        <br />
        “…so that it [wealth] will not be a perpetual distribution among the
        rich from among you…”(Quran 59:7).
        <br />
        Yet fiat money does exactly that, ensuring wealth concentrates in the
        hands of those closest to the money printer: governments, banks, and
        large corporations. This system fosters despair among ordinary people
        who feel they are running on a treadmill, working harder each year only
        to fall behind as prices rise faster than wages. In such an environment,
        Riba becomes not just a financial tool but a weapon of control. For
        decades Muslims and non-Muslims alike had no robust alternative to this
        global trap. Gold and silver, once traditional stores of value, were
        demonetized and restricted, leaving only fiat currencies to circulate.
        Muslims who wished to avoid Riba found no practical means to opt out of
        the system, despite knowing that Allah declared war on those who persist
        in usury. The tension between Islamic law and the global financial
        system has remained unresolved for generations, until the emergence of
        Bitcoin offered a way out.
        <br />
        <br />
        <b>Bitcoin and the Principle of Proof of Work</b>
        <br />
        <br />
        The arrival of Bitcoin marks a turning point for Muslims seeking to
        align their financial lives with the teachings of Islam. Unlike fiat
        money, which is created without effort and backed only by decrees of
        central planners, Bitcoin comes into existence through a process that
        requires genuine work. This mechanism, known as Proof of Work, requires
        miners to expend energy and computational power to secure the network
        and bring new coins into circulation. In Islamic terms, this process
        aligns more closely with the concept of earning through labor, where
        wealth is tied to effort and resources expended. The Prophet Muhammad
        (peace be upon him) said:
        <br />
        “No one has ever eaten better food than that which he eats from the work
        of his own hands.” (Sahih Bukhari).
        <br />
        Bitcoin embodies this principle, as each coin is mined through work
        performed and energy consumed, not through usurious contracts.
        <br />
        Another essential feature of Bitcoin is its fixed supply. Unlike fiat
        currencies that can be inflated indefinitely, Bitcoin is capped at 21
        million coins. This scarcity makes it resistant to the inflationary
        pressures that rob savers under fiat systems. For the first time in
        modern history, Muslims have access to a monetary network that
        encourages savings instead of punishing them. The Quran commands
        believers not to squander their wealth:
        <br />
        “And do not spend wastefully. Indeed, the wasteful are brothers of the
        devils…” (Quran 17:26-27).
        <br />
        Fiat systems, through inflation, encourage reckless consumption and
        wastefulness, as holding onto savings results in guaranteed losses.
        Bitcoin reverses this paradigm by rewarding those who save and
        discouraging reckless debt.
        <br />
        Furthermore, the capped supply of Bitcoin creates an environment where
        purchasing power increases as demand rises. This has profound
        implications for Muslims seeking to avoid Riba. In a fiat system, people
        are forced to lend money at interest just to preserve value. In Bitcoin,
        the appreciation of savings can outperform interest rates, making
        lending without usury a viable alternative. This aligns perfectly with
        the Islamic vision of trade and partnerships built on shared risk and
        equity rather than guaranteed interest. Instead of profiting from
        someone else’s hardship, a Muslim can simply hold Bitcoin and see their
        wealth grow with the natural increase in demand. In effect, Bitcoin
        makes it possible to build financial relationships that are cooperative
        rather than exploitative, freeing Muslims from the coercive logic of
        debt and interest.
        <br />
        <br />
        <b>A Tool for Muslim Societies to Exit the Riba System</b>
        <br />
        <br />
        Bitcoin provides Muslims and Islamic societies with a practical tool to
        build a parallel monetary network that is entirely free of Riba. For the
        first time in centuries, Muslims are no longer confined to choosing
        between fiat currencies, each of which is entangled in usury. They can
        now opt out of the system and embrace a form of money that is both
        ethical and technologically advanced. By adopting Bitcoin, Muslim
        individuals, communities, and eventually entire nations can escape the
        cycle of debt and inflation that has enslaved humanity. The Quranic
        principle of justice in financial matters can finally be realized
        through the use of a monetary network that is incorruptible,
        transparent, and beyond the control of exploitative elites.
        <br />
        Muslim nations have the opportunity to lead in this transformation.
        Instead of importing fiat practices and strengthening ties to debt-based
        systems, they can foster adoption of Bitcoin to encourage savings,
        trade, and long-term stability. The Prophet Muhammad (peace be upon him)
        said:
        <br />
        “A time will come upon the people when they will consume Riba.” It was
        asked, “All of them?” He replied: “Whoever does not consume it will be
        affected by its dust.” (Sunan Abu Dawood).
        <br />
        This Hadith accurately describes the reality of today’s Muslims, who,
        even if they avoid explicit interest, are still harmed by the
        inflationary dust of fiat. Bitcoin offers a way out of this condition,
        giving Muslims the ability to avoid not only direct Riba but its
        indirect consequences as well.
        <br />
        Moreover, Bitcoin promotes financial sovereignty and dignity. Instead of
        being at the mercy of international financial institutions that impose
        conditions tied to usurious loans, Muslim societies can build wealth
        from within, trading directly with one another and preserving value
        across generations. The Quran praises those who avoid Riba: <br />
        “But whoever gives up [Riba], his wealth is for him, and his affair
        rests with Allah.” (Quran 2:275).
        <br />
        By integrating Bitcoin into their economies, Muslims can lay the
        foundation for an economic system that embodies this verse. It will not
        be easy, as entrenched powers will resist any movement away from fiat
        and debt. However, history teaches us that every revival begins with
        individuals who recognize truth and act upon it. Just as the early
        Muslims embraced gold and silver as honest forms of money, modern
        Muslims can embrace Bitcoin as an honest form of digital money.
        <br />
        Bitcoin is not simply a technological innovation but a profound
        opportunity for Muslims to fulfill their religious obligation to avoid
        Riba. It is money created through work, not usury. It is money that
        encourages savings and equity, not debt and exploitation. It is money
        with a capped supply that ensures justice for savers and protection
        against inflationary theft. For generations, Muslims lacked a practical
        way to opt out of the riba-based system, but with Bitcoin, that barrier
        has been removed. The path is now open for Islamic societies to lead the
        world in building an ethical, transparent, and riba-free financial
        system that aligns with divine guidance.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today's lesson emphasized how the global fiat monetary system is
        inseparably built on Riba, as new money is created through usurious debt
        and the silent theft of inflation. This structure forces Muslims into a
        cycle of debt and discourages savings, which directly contradicts
        Islamic teachings that strictly prohibit Riba. The lesson showed that
        Bitcoin offers a way out by providing a monetary network based on proof
        of work, capped supply, and fairness in trade. Unlike fiat, it cannot be
        inflated, it encourages savings, and it enables Muslims to build a
        parallel, riba-free economic system that aligns with the principles of
        justice and honesty enshrined in the Quran and Sunnah. Bitcoin,
        therefore, stands as a practical tool for Muslims to avoid Riba and
        reclaim financial sovereignty in line with their faith.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        As we have seen, Bitcoin offers Muslims a way to escape the chains of
        riba by providing a monetary system that is just, transparent, and
        rooted in real work rather than usurious debt. But the impact of Bitcoin
        on Muslim life goes even further than simply avoiding what is forbidden.
        In the next lesson, we will explore how Bitcoin nurtures moderation,
        patience, and foresight in financial planning. Unlike fiat money that
        loses value and pushes people toward reckless consumerism, Bitcoin
        rewards those who save, invest wisely, and think of future generations.
        Day 66 will show how Bitcoin helps Muslims live in accordance with the
        Qur’anic guidance of balance and moderation, ensuring that wealth is
        used for what is beneficial and lasting.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default IslamicGuide65;
